import React from "react";

const normalizeSectionId = (section) => {
  if (!section) return null;
  return String(section.section_id ?? section.id ?? section.sectionId ?? "");
};

const getSectionIndexFromBeats = (sections, beatsElapsed) => {
  if (!sections?.length || !Number.isFinite(beatsElapsed)) return null;
  const current = sections.find((section) => {
    const startBeat = Number(section.start_beat ?? section.startBeat ?? 0);
    const endBeatRaw = section.end_beat ?? section.endBeat;
    const endBeat = Number.isFinite(Number(endBeatRaw)) ? Number(endBeatRaw) : Infinity;
    return beatsElapsed >= startBeat && beatsElapsed < endBeat;
  });
  return normalizeSectionId(current);
};

const getEstimatedAudioTime = (transportClock) => {
  if (!transportClock) return null;
  const { audioTimeSec, updatedAtMs } = transportClock;
  if (!Number.isFinite(audioTimeSec) || !Number.isFinite(updatedAtMs)) return null;
  const deltaSec = (Date.now() - updatedAtMs) / 1000;
  return audioTimeSec + Math.max(0, deltaSec);
};

export default function useProjectorTimelineSync({
  timelineSections,
  bpm,
  isLitePlaybackActive,
  transportStartAt,
  transportStartBeat = 0,
  transportClock,
  sectionIndexById,
  onSectionIndex,
  onEnd,
  manualOverrideRef,
}) {
  const rafRef = React.useRef(null);
  const lastAutoIndexRef = React.useRef(null);
  const lastStatusRef = React.useRef("");
  const lastWarnAtRef = React.useRef(0);

  React.useEffect(() => {
    if (!isLitePlaybackActive) return;
    if (!timelineSections?.length) {
      if (lastStatusRef.current !== "missing-sections") {
        lastStatusRef.current = "missing-sections";
        console.log("[projector-sync] waiting: missing timeline sections");
      }
      return;
    }
    if (!Number.isFinite(bpm) || bpm <= 0) {
      if (lastStatusRef.current !== "missing-bpm") {
        lastStatusRef.current = "missing-bpm";
        console.log("[projector-sync] waiting: missing bpm");
      }
      return;
    }
    if (!Number.isFinite(transportStartAt)) {
      if (lastStatusRef.current !== "missing-start") {
        lastStatusRef.current = "missing-start";
        console.log("[projector-sync] waiting: missing transport start");
      }
      return;
    }

    const secondsPerBeat = 60 / bpm;
    lastStatusRef.current = "running";

    const tick = () => {
      const audioNow = getEstimatedAudioTime(transportClock);
      if (Number.isFinite(audioNow)) {
        const beatsElapsed = Number(transportStartBeat || 0) + (audioNow - transportStartAt) / secondsPerBeat;
        const maxEndBeat = Math.max(
          ...timelineSections.map((section) => {
            const endBeatRaw = section.end_beat ?? section.endBeat;
            const endBeat = Number.isFinite(Number(endBeatRaw)) ? Number(endBeatRaw) : -Infinity;
            return endBeat;
          }),
        );
        if (Number.isFinite(maxEndBeat) && beatsElapsed >= maxEndBeat) {
          if (lastStatusRef.current !== "ended") {
            lastStatusRef.current = "ended";
            console.log("[projector-sync] end reached", { beatsElapsed, maxEndBeat });
          }
          onEnd?.();
          return;
        }
        const sectionId = getSectionIndexFromBeats(timelineSections, beatsElapsed);
        if (manualOverrideRef?.current) {
          manualOverrideRef.current.lastAutoSectionId = sectionId;
        }
        if (sectionId && sectionIndexById?.has(sectionId)) {
          const nextIndex = sectionIndexById.get(sectionId);
          if (nextIndex !== lastAutoIndexRef.current) {
            lastAutoIndexRef.current = nextIndex;
            onSectionIndex(nextIndex, sectionId);
          }
        } else if (!sectionId) {
          const now = Date.now();
          if (now - lastWarnAtRef.current > 2000) {
            lastWarnAtRef.current = now;
            console.log("[projector-sync] no section match", { beatsElapsed });
          }
        } else if (!sectionIndexById?.has(sectionId)) {
          const now = Date.now();
          if (now - lastWarnAtRef.current > 2000) {
            lastWarnAtRef.current = now;
            console.log("[projector-sync] missing section index", { sectionId });
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [
    bpm,
    isLitePlaybackActive,
    manualOverrideRef,
    onEnd,
    onSectionIndex,
    sectionIndexById,
    timelineSections,
    transportClock,
    transportStartAt,
    transportStartBeat,
  ]);
}
